function addAmountsToAccounts(accounts, processed) {
  accounts.forEach(account => {
    let yearTotals = {};

    processed.forEach(procAccount => {
      if (procAccount.chart_of_account_id === account.id) {
        const locationId = procAccount.location_id;
        if (!yearTotals[locationId]) yearTotals[locationId] = 0;

        const yearTotal = parseFloat(procAccount.year_total) || 0;

        yearTotals[locationId] += yearTotal;
      }
    });

    account.yearTotals = yearTotals;

    // Si tiene hijos, añadir cantidades a los hijos
    if (account.children && account.children.length > 0) {
      addAmountsToAccounts(account.children, processed);
    }

    // Añadir nodos de subcategorías directamente bajo la cuenta principal
    let subcategoryMap = {};

    processed.forEach(procAccount => {
      if (procAccount.chart_of_account_id === account.id && procAccount.subcategory_id) {
        const locationId = procAccount.location_id;

        // Crear o actualizar la subcategoría
        if (!subcategoryMap[procAccount.subcategory_id]) {
          subcategoryMap[procAccount.subcategory_id] = {
            id: procAccount.subcategory_id,
            name: procAccount.subcategory_name,
            parent: account.id,
            children: [],
            yearTotals: {},
            isSynthetic: true,
            level: account.level + 1
          };
        }

        let subcategoryNode = subcategoryMap[procAccount.subcategory_id];

        // Actualizar los montos en la subcategoría
        subcategoryNode.yearTotals[locationId] = (subcategoryNode.yearTotals[locationId] || 0) + parseFloat(procAccount.year_total) || 0;
      }
    });

    // Añadir las subcategorías al árbol de cuentas principal
    for (let subcategoryId in subcategoryMap) {
      account.children.push(subcategoryMap[subcategoryId]);
    }
  });
}

function sumChildrenAmounts(node) {
  let yearTotals = { ...(node.yearTotals || {}) };

  const realChildren = (node.children || []).filter(c => !c.isSynthetic);
  realChildren.forEach(child => {
    const { yearTotals: childYearTotals } = sumChildrenAmounts(child);
    for (const locationId in childYearTotals) {
      if (!yearTotals[locationId]) yearTotals[locationId] = 0;
      yearTotals[locationId] += childYearTotals[locationId];
    }
  });

  node.yearTotals = yearTotals;

  return { yearTotals };
}

function shouldIncludeNode(node) {
  const alwaysInclude = [2, 3, 4].includes(node.id);
  if (alwaysInclude) return true;

  const hasNonZeroTotals = Object.values(node.yearTotals).some(total => total !== 0);
  if (hasNonZeroTotals) return true;

  if (node.children && node.children.length > 0) {
    return node.children.some(shouldIncludeNode);
  }

  return false;
}

function flattenTree(data, level = 0, result = [], parentNature = 1) {
  data.forEach(node => {
    const nodeNature = node.nature || parentNature;
    if (shouldIncludeNode(node)) {
      const filteredChildren = node.children ? flattenTree(node.children, level + 1, [], nodeNature) : [];

      result.push({
        id: node.id,
        name: node.name,
        parent: node.parent,
        nature: nodeNature,
        code: node.code,
        note: node.note,
        account_type_id: node.account_type_id,
        dgii_category_id: node.dgii_category_id,
        status: node.status,
        level: level,
        yearTotals: node.yearTotals,
        isParent: filteredChildren.length > 0,
        children: filteredChildren
      });
    }
  });
  return result;
}

export function generateEstadoSituacionView(all_accounts, processed_accounts, yearTotals) {
  addAmountsToAccounts(all_accounts, processed_accounts);

  all_accounts.forEach(node => sumChildrenAmounts(node));

  // Asegurarse de incluir todas las cuentas, incluso las sin hijos
  const allFlattenedAccounts = flattenTree(all_accounts);
  const rootAccounts = allFlattenedAccounts.filter(account => account.level === 0); // Asegurarse de incluir solo el nivel raíz

  const activosNode = allFlattenedAccounts.find(account => account.id === 2);
  const pasivosNode = allFlattenedAccounts.find(account => account.id === 3) || { yearTotals: {} };
  const patrimonioNode = allFlattenedAccounts.find(account => account.id === 4) || { yearTotals: {} };
  
  if (activosNode) activosNode.nature = 1;
  if (pasivosNode) pasivosNode.nature = -1;
  if (patrimonioNode) patrimonioNode.nature = -1;

  const utilidadesPeriodoActual = yearTotals || {};

  // Extract all unique locationIds
  const locationIds = new Set([
     ...Object.keys(utilidadesPeriodoActual),
     ...Object.keys(patrimonioNode.yearTotals),
     ...Object.keys(pasivosNode.yearTotals)
  ]);

  // Calcular TOTAL PARTICIPACIÓN y TOTAL PASIVOS Y PARTICIPACIÓN
  let totalParticipacion = {};
  let totalPasivosParticipacion = {};

  locationIds.forEach(locationId => {
    totalParticipacion[locationId] = (patrimonioNode.yearTotals[locationId] || 0) + (utilidadesPeriodoActual[locationId] || 0);
    totalPasivosParticipacion[locationId] = totalParticipacion[locationId] + (pasivosNode.yearTotals[locationId] || 0);
  });

  rootAccounts.push({
    id: 'utilidades',
    name: 'Utilidades Periodo Actual',
    parent: null,
    nature: -1,
    code: '',
    note: '',
    account_type_id: null,
    dgii_category_id: null,
    status: 'utilidades',
    level: 0,
    yearTotals: utilidadesPeriodoActual,
    isParent: false,
    children: []
  });

  rootAccounts.push({
    id: 'total_participacion',
    name: 'TOTAL PARTICIPACIÓN',
    parent: null,
    nature: -1,
    code: '',
    note: '',
    account_type_id: null,
    dgii_category_id: null,
    status: 'total_participacion',
    level: 0,
    yearTotals: totalParticipacion,
    isParent: false,
    isTotal: true,
    children: []
  });

  rootAccounts.push({
    id: 'total_pasivos_participacion',
    name: 'TOTAL PASIVOS Y PARTICIPACIÓN',
    parent: null,
    nature: -1,
    code: '',
    note: '',
    account_type_id: null,
    dgii_category_id: null,
    status: 'total_pasivos_participacion',
    level: 0,
    yearTotals: totalPasivosParticipacion,
    isParent: false,
    isTotal: true,
    children: []
  });

  return rootAccounts;
}
